// Sprint 10.9 — Market expansion waitlist config.
// One config object per new market. `WaitlistLanding` reads this and renders
// a market-specific landing page. All markets share the unified
// `waitlist.eta.coming_2027` i18n badge and the same POST /api/waitlist/{market}.
//
// HK config note: Hong Kong is a **waitlist-only** market for the South Asian
// diaspora. Copy MUST NOT reference HKUST, PhD, researcher, or the founder's
// son — those references are legally sensitive due to scholarship terms.

export const MARKET_CONFIG = {
  my: {
    slug: 'my',
    testidPrefix: 'my',
    flag: '🇲🇾',
    nativeName: 'Malaysia',
    hero: {
      title: 'Genius One Plus is coming to Malaysia',
      sub: 'Kuala Lumpur \u00b7 Penang \u00b7 Johor Bahru — join the waitlist',
    },
    waitlistTitle: 'Notify me when we launch in Malaysia',
    waitlistDone: 'You are on the list. We will WhatsApp you when we launch.',
    phonePlaceholder: '+60 12 345 6789',
    // Malaysia dial code — front-end normaliser prepends `+60` if the user
    // types local digits without the country code.
    countryCode: '60',
    palette: 'from-rose-50 via-amber-50 to-yellow-50',
    accent: 'bg-rose-200 text-rose-900',
    heroImage: '/marketing/hero-08-ai-dial-watercolor.jpeg',
    pricingHint: 'RM (ringgit) pricing to be announced closer to launch.',
  },
  'in-hi': {
    slug: 'in-hi',
    testidPrefix: 'in-hi',
    flag: '🇮🇳',
    nativeName: 'Hindi belt (Delhi \u00b7 UP \u00b7 Bihar \u00b7 MP)',
    hero: {
      title: 'Genius One Plus is coming to the Hindi belt',
      sub: 'Delhi \u00b7 Lucknow \u00b7 Patna \u00b7 Bhopal — join the waitlist',
    },
    waitlistTitle: 'Notify me when we launch',
    waitlistDone: 'You are on the list. We will WhatsApp you when we launch.',
    phonePlaceholder: '+91 98123 45678',
    countryCode: '91',
    palette: 'from-orange-50 via-amber-50 to-emerald-50',
    accent: 'bg-orange-200 text-orange-900',
    heroImage: '/marketing/hero-08-ai-dial-watercolor.jpeg',
    pricingHint: '\u20b9 (rupee) pricing to be announced closer to launch.',
  },
  id: {
    slug: 'id',
    testidPrefix: 'id',
    flag: '🇮🇩',
    nativeName: 'Indonesia',
    hero: {
      title: 'Genius One Plus is coming to Indonesia',
      sub: 'Jakarta \u00b7 Surabaya \u00b7 Bandung — join the waitlist',
    },
    waitlistTitle: 'Notify me when we launch in Indonesia',
    waitlistDone: 'You are on the list. We will WhatsApp you when we launch.',
    phonePlaceholder: '+62 812 3456 7890',
    countryCode: '62',
    palette: 'from-red-50 via-white to-red-50',
    accent: 'bg-red-200 text-red-900',
    heroImage: '/marketing/hero-08-ai-dial-watercolor.jpeg',
    pricingHint: 'Rp (rupiah) pricing to be announced closer to launch.',
  },
  th: {
    slug: 'th',
    testidPrefix: 'th',
    flag: '🇹🇭',
    nativeName: 'Thailand',
    hero: {
      title: 'Genius One Plus is coming to Thailand',
      sub: 'Bangkok \u00b7 Chiang Mai \u00b7 Phuket — join the waitlist',
    },
    waitlistTitle: 'Notify me when we launch in Thailand',
    waitlistDone: 'You are on the list. We will WhatsApp you when we launch.',
    phonePlaceholder: '+66 81 234 5678',
    countryCode: '66',
    palette: 'from-sky-50 via-white to-red-50',
    accent: 'bg-sky-200 text-sky-900',
    heroImage: '/marketing/hero-08-ai-dial-watercolor.jpeg',
    pricingHint: '\u0e3f (baht) pricing to be announced closer to launch.',
  },
  pk: {
    slug: 'pk',
    testidPrefix: 'pk',
    flag: '🇵🇰',
    nativeName: 'Pakistan',
    hero: {
      title: 'Genius One Plus is coming to Pakistan',
      sub: 'Karachi \u00b7 Lahore \u00b7 Islamabad — join the waitlist',
    },
    waitlistTitle: 'Notify me when we launch in Pakistan',
    waitlistDone: 'You are on the list. We will WhatsApp you when we launch.',
    phonePlaceholder: '+92 300 1234567',
    countryCode: '92',
    palette: 'from-emerald-50 via-white to-emerald-50',
    accent: 'bg-emerald-200 text-emerald-900',
    heroImage: '/marketing/hero-08-ai-dial-watercolor.jpeg',
    pricingHint: 'PKR (rupee) pricing to be announced closer to launch. Full Urdu locale coming soon.',
  },
  // ------------------------------------------------------------------- //
  // Hong Kong — SOUTH ASIAN DIASPORA ONLY. Copy is legally reviewed:
  // NO references to HKUST, PhD, researcher, or the founder's son.
  // ------------------------------------------------------------------- //
  hk: {
    slug: 'hk',
    testidPrefix: 'hk',
    flag: '🇭🇰',
    nativeName: 'Hong Kong',
    hero: {
      title: 'Genius One Plus is coming to Hong Kong',
      sub: 'For South Asian families in Hong Kong — kids aged 10\u201312, taught in English, tuned for parents who speak Sinhala, Tamil, Hindi, Urdu, or Bengali at home.',
    },
    waitlistTitle: 'Notify me when we launch in Hong Kong',
    waitlistDone: 'You are on the list. We will WhatsApp you when we launch.',
    phonePlaceholder: '+852 5123 4567',
    countryCode: '852',
    palette: 'from-pink-50 via-white to-amber-50',
    accent: 'bg-pink-200 text-pink-900',
    heroImage: '/marketing/hero-08-ai-dial-watercolor.jpeg',
    pricingHint: 'HKD pricing to be announced closer to launch. Waitlist-only market.',
  },
};

export function getMarketConfig(slug) {
  return MARKET_CONFIG[slug] || null;
}
